/**
 * Heavy `POLYLINE` × `OFFSET` / `ARRAYPATH` — the ONE truth for "which heavy
 * polyline these two commands accept, and why they decline the rest".
 *
 * `@codex PR #785 round 14, P2` — two findings with one root:
 *
 *  - *"Support 2D heavy polylines in OFFSET"* — `OFFSET_AND_ARRAYPATH_
 *    SUPPORTED_TYPES` (`apps/web/.../scene/draftModifyStateMachine.ts`) ended
 *    at `LWPOLYLINE`, so the selection picker filtered every `Polyline` out
 *    before the distance comparison, and an agent / direct dispatch fell
 *    through `geometryReducerEdit.ts`'s unsupported-type `default:` and
 *    appended nothing. A straight, default-extrusion heavy polyline therefore
 *    could not be offset at all, despite having exactly the applicable
 *    operation its lightweight counterpart has.
 *  - *"Accept 2D heavy polylines as ARRAYPATH curves"* — the same picker
 *    constant, plus `arrayReducers.ts`'s `isPathEntity`, which recognised only
 *    Line / Circle / Arc / LWPOLYLINE. A visible heavy polyline — including one
 *    whose bulged path the round-3 topology and round-5 metric code already
 *    understand — could not drive ARRAYPATH.
 *
 * Both are fixed by PROJECTING the 2D heavy subtype onto the LWPOLYLINE path
 * this package already has: a 2D heavy polyline's planar geometry IS vertices
 * plus per-vertex group-42 bulges, i.e. LWPOLYLINE's geometry, so
 * `./heavyPolylineDrawnChain.ts` flattens it and the EXISTING offset ring and
 * EXISTING arc-length sampler (`./pathSampling.ts`) consume it unchanged. No
 * second offset algorithm and no second sampler exist.
 *
 * ## Which subtype each command accepts — {@link heavyPolylineCurveRefusal}
 *
 * The two commands differ, so the predicate takes the ROLE. The difference is
 * not a preference: OFFSET WRITES a new record run back, ARRAYPATH only READS
 * the curve.
 *
 * | subtype (group 70)            | OFFSET                 | ARRAYPATH path      |
 * |-------------------------------|------------------------|---------------------|
 * | 2D polyline                   | yes                    | yes                 |
 * | 2D polyline, curve/spline-fit | REFUSED (`"fit"`)      | yes                 |
 * | 3D polyline                   | REFUSED (`"polyline-3d"`) | REFUSED (`"polyline-3d"`) |
 * | polygon / poly-face mesh      | REFUSED (`"mesh"`)     | REFUSED (`"mesh"`)  |
 *
 * ### Two of those refusals are what AutoCAD ITSELF does
 *
 *  - **OFFSET × 3D polyline and × either mesh.** The Autodesk OFFSET command
 *    reference lists the objects OFFSET accepts: line, arc, circle, ellipse,
 *    elliptical arc, **2D polyline**, construction line (xline), ray and
 *    spline. A 3D polyline and a polygon / poly-face mesh are not on it, and
 *    AutoCAD answers a pick of one with *"Cannot offset that object."* —
 *    a VISIBLE decline, not a silent skip. `ezdxf` agrees by construction: its
 *    offset helpers (`ezdxf.path`, `ezdxf.math.offset_vertices_2d`) are
 *    documented and typed as 2D.
 *  - **ARRAYPATH × either mesh.** The Autodesk ARRAYPATH reference lists the
 *    valid path curves: line, polyline, 3D polyline, spline, helix, arc, circle
 *    or ellipse. A mesh is not a curve, has no traversal order at all
 *    (`heavyPolylineRuns` returns rows, columns and face edges for one), and is
 *    rejected by AutoCAD too.
 *
 * These two are therefore FULL AutoCAD parity, not gaps: the only thing this
 * repo had to add was making the decline VISIBLE (an RU+EN warning-tone audit
 * row through `apps/web/.../state/geometryShellReducer.ts`) instead of a
 * picker filter with no user-facing reason.
 *
 * ### Two are KNOWN-INCOMPLETE SUBSYSTEMS, documented with their completion plan
 *
 * CLAUDE.md ПРИОРИТЕТ #1's one allowed exception — the same shape
 * `./tableScaleRefusal.ts` and `./heavyPolylineTrim.ts` use:
 *
 *  - **`"fit"` × OFFSET.** A curve-fit (bit 2) or spline-fit (bit 4) polyline's
 *    record run is the OUTPUT of a fitting pass, and AutoCAD's OFFSET of one
 *    produces a NEW fitted polyline — it re-fits, it does not translate the
 *    generated vertices. This codebase has no polyline fitter, and offsetting
 *    the generated run in place would emit a record set that is the output of
 *    no fit while group 70 still claims it is (and, for a spline-fit polyline,
 *    writing back only the VISIBLE run would DELETE the bit-16 control FRAME
 *    records outright — the same loss `./heavyPolylineTrim.ts` refuses for a
 *    TRIM target). COMPLETING IT: a polyline curve/spline fitter (PEDIT Fit /
 *    Spline) that regenerates the run AND its frame from the offset control
 *    vertices, wired so the refit and the vertex write land in ONE
 *    `EntityMutation`; then this arm is deleted and the OFFSET column above
 *    becomes "yes" for the fitted row. ARRAYPATH needs none of that, which is
 *    why it accepts a fitted polyline TODAY: it samples the drawn
 *    approximation `heavyPolylineRuns` already resolves and mutates nothing.
 *  - **`"polyline-3d"` × ARRAYPATH.** AutoCAD DOES accept a 3D polyline as an
 *    array path. This package's sampler is 2D end to end — `PathSample.point`
 *    is `{x, y}`, `pathTotalLength` measures in the XY plane, and
 *    `arrayReducers.ts` places each clone with a 2D `translatePoint`. Sampling
 *    a 3D polyline through it would walk the XY SHADOW of the curve: the arc
 *    length would be short by the climb, every division would land at the wrong
 *    parameter, and each copy's Z would be silently dropped — a confidently
 *    wrong array rather than a missing one. COMPLETING IT: give `PathSample` a
 *    Z channel and `pathTotalLength` a 3D chord sum, then let `arrayReducers`
 *    translate in three axes — which first needs the editor's one
 *    `TransformFn` (`./geometryTransforms.ts`) widened to move an entity in Z
 *    at all; the same widening `./heavyPolylineTrim.ts` describes for its own
 *    `"polyline-3d"` arm. `./arrayPathPlaneRefusal.ts` carries the full plan,
 *    because the SAME gap reaches the other path kinds through their group-210
 *    plane rather than through their subtype (@codex PR #785 round 16, P2) and
 *    the two arms have to be completed and deleted together.
 *
 * ## The FRAME is a separate question, answered next door
 *
 * This predicate is the SUBTYPE half of ARRAYPATH's eligibility only. Whether
 * the accepted curve's PLANE is one the 2D sampler can place copies along —
 * a tilted CIRCLE / ARC / LWPOLYLINE / 2D heavy POLYLINE, or a LINE that
 * climbs — is `./arrayPathPlaneRefusal.ts`'s single truth, asked beside this
 * one by `arrayReducers.ts`. Kept apart so each stays exhaustive over its own
 * question: a mesh has no traversal whatever its normal, and a tilted circle is
 * declined for a reason that has nothing to do with heavy polylines.
 */

import {
  polylineSubtype,
  type DrawingEntity,
  type DrawingSpace,
  type PolylineEntity,
} from "@dxf-ai-editor/drawing-model";

import { isHeavyPolylineFitted } from "./heavyPolylineDrawnChain";

/**
 * The two curve commands this predicate answers for. Separate because OFFSET
 * WRITES a record run back and ARRAYPATH only READS the curve — see the table
 * in the module header.
 */
export type HeavyPolylineCurveRole = "offset" | "array-path";

/**
 * Why `OFFSET` declines this heavy polyline. `null` means it is fully
 * supported.
 */
export type HeavyPolylineOffsetRefusal = "mesh" | "polyline-3d" | "fit";

/**
 * Why `ARRAYPATH` declines this heavy polyline as a path curve. `null` means it
 * is fully supported.
 *
 * NARROWER than {@link HeavyPolylineOffsetRefusal} by exactly `"fit"`, and that
 * is a TYPE-LEVEL fact, not a comment: ARRAYPATH only READS the curve, so it
 * samples a curve-fit / spline-fit polyline's drawn approximation happily and
 * can never decline one. Stating it here is what lets the shell reducer's
 * ARRAYPATH audit switch be EXHAUSTIVE over two cases instead of carrying a
 * third, unreachable arm pointing at a message that could never be shown.
 */
export type HeavyPolylineArrayPathRefusal = "mesh" | "polyline-3d";

/** Either command's verdict, for callers that are generic over the role. */
export type HeavyPolylineCurveRefusal =
  | HeavyPolylineOffsetRefusal
  | HeavyPolylineArrayPathRefusal;

/**
 * `null` when `role` fully supports `entity`, otherwise the reason it is
 * declined. See the module header for each reason's source or completion plan.
 *
 * Total and pure — the SINGLE predicate the document reducers' guards
 * (`geometryReducerEdit.ts`'s OFFSET arm, `arrayReducers.ts`'s `isPathEntity`)
 * and the shell reducer's audit message all call, so the three can never drift
 * into "reported a refusal the document did not make" or "stayed silent about
 * one it did" (the two-site shape `./tableScaleRefusal.ts` established).
 */
export function heavyPolylineCurveRefusal(
  entity: Pick<PolylineEntity, "flags">,
  role: "offset",
): HeavyPolylineOffsetRefusal | null;
export function heavyPolylineCurveRefusal(
  entity: Pick<PolylineEntity, "flags">,
  role: "array-path",
): HeavyPolylineArrayPathRefusal | null;
export function heavyPolylineCurveRefusal(
  entity: Pick<PolylineEntity, "flags">,
  role: HeavyPolylineCurveRole,
): HeavyPolylineCurveRefusal | null;
export function heavyPolylineCurveRefusal(
  entity: Pick<PolylineEntity, "flags">,
  role: HeavyPolylineCurveRole,
): HeavyPolylineCurveRefusal | null {
  const subtype = polylineSubtype(entity);
  // A mesh and a 3D polyline are declined by BOTH commands, so these two tests
  // come first and are role-blind. They are declined for DIFFERENT reasons in
  // the two roles (AutoCAD parity for three of the four pairs, a documented 2D
  // sampler for ARRAYPATH × 3D) — the reason is the message's job, not the
  // predicate's.
  if (subtype === "polygon-mesh" || subtype === "polyface-mesh") return "mesh";
  if (subtype === "3d") return "polyline-3d";
  // Tested POSITIVELY (`=== "offset"`), not as "anything that is not an array
  // path": the fit refusal is OFFSET's alone because only OFFSET writes the run
  // back, and stating which role owns it keeps the predicate honest if a third
  // curve command is ever added.
  if (role === "offset" && isHeavyPolylineFitted(entity)) return "fit";
  return null;
}

/**
 * `true` when `entity` is a heavy `POLYLINE` that `role` fully supports — the
 * narrowing form the document reducers use, so an eligibility test and the
 * refusal message can never disagree.
 */
export function isSupportedHeavyPolylineCurve(
  entity: DrawingEntity,
  role: HeavyPolylineCurveRole,
): entity is PolylineEntity {
  return (
    entity.type === "Polyline" && heavyPolylineCurveRefusal(entity, role) === null
  );
}

/**
 * Resolve the ONE entity id an `OFFSET` / `ARRAYPATH` dispatch names as its
 * curve and report why the command declines it, or `null` when it does not.
 *
 * `null` covers three cases that are all "nothing to report": the id resolves
 * to no entity, it resolves to something that is not a heavy polyline (an
 * LWPOLYLINE, a Line, …), or it resolves to a heavy polyline the role accepts.
 *
 * ONE document walk. Unlike {@link import("./selectionRefusalWalk").summarizeSelectionRefusal},
 * which splits a SELECTION into declined and transformed, both of these
 * commands take a SINGLE curve id — `geometry/offset-entity`'s `entityId` and
 * `geometry/create-arraypath`'s `pathEntityId` — so there is no partition to
 * report, only that one id's verdict, and reusing the selection walk would
 * force a caller to unpack a two-field summary of a one-entity question.
 */
export function heavyPolylineCurveRefusalFor(
  spaces: readonly DrawingSpace[],
  entityId: string,
  role: "offset",
): HeavyPolylineOffsetRefusal | null;
export function heavyPolylineCurveRefusalFor(
  spaces: readonly DrawingSpace[],
  entityId: string,
  role: "array-path",
): HeavyPolylineArrayPathRefusal | null;
export function heavyPolylineCurveRefusalFor(
  spaces: readonly DrawingSpace[],
  entityId: string,
  role: HeavyPolylineCurveRole,
): HeavyPolylineCurveRefusal | null {
  for (const space of spaces) {
    for (const entity of space.entities) {
      if (entity.id !== entityId) continue;
      if (entity.type !== "Polyline") return null;
      return heavyPolylineCurveRefusal(entity, role);
    }
  }
  return null;
}
